import { useCallback, useMemo, useRef, useState } from "react";

const STORAGE_KEY = "pixley_feed_monster_muted";

// Munchy's real character voice — the same ElevenLabs voice used in the Pixley
// iOS app ("Goofy — Nasal, Fast-Paced and Energetic", saved to the Pixley
// account as "Munchy - Feed the Monster"). Every spoken line is rendered by the
// backend's POST /elevenlabs/quick-tts (fast flash model) and cached per line,
// so each unique line costs one generation per session. On a cache miss the
// game speaks instantly through the browser's on-device synthesizer (goofy
// monster pitch) while the real clip downloads quietly for next time; offline
// or signed out, the game simply sounds like it did before.
const MUNCHY_VOICE_ID = "BRruTxiLM2nszrcCIpz1";
const FALLBACK_PITCH = 0.8; // goofy Munchy larynx for the synth understudy
// Munchy speaks EVERY line himself — greetings, prompts, praise, hints, and the
// food words a child taps to hear. There is no separate narrator voice, so the
// child only ever hears Munchy.

// Module-level so warmed clips survive component remounts within a session.
const clipCache = new Map(); // `${voiceId}::${text}` -> object URL for the mp3
const inFlight = new Map(); // `${voiceId}::${text}` -> Promise<string|null> in flight
const clipKey = (voiceId, text) => `${voiceId}::${text}`;

const base64ToBlobUrl = (base64, mediaType = "audio/mpeg") => {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], { type: mediaType }));
};

// Everything else (pops, chomps, wrong buzzes, celebration jingles) is a
// lightweight WebAudio oscillator tone, so the effects layer works offline.
const createAudioContext = () => {
  if (typeof window === "undefined") return null;
  const AudioContext = window.AudioContext || window.webkitAudioContext;
  return AudioContext ? new AudioContext() : null;
};

// A tiny silent (but NOT muted — iOS ignores muted playback) WAV, played on the
// shared <audio> element inside the "Let's feed Munchy!" tap. That gesture-driven
// play unlocks the element under strict autoplay policies (iOS Safari), so the
// greeting clip — which usually finishes downloading a beat AFTER the tap — can
// still play in Munchy's real voice instead of being blocked into the synth.
const SILENT_WAV_URL = (() => {
  try {
    if (typeof window === "undefined" || typeof URL === "undefined" || typeof Blob === "undefined") return "";
    const samples = 800; // ~0.1s of 8 kHz silence — long enough to satisfy the unlock
    const bytes = new Uint8Array(44 + samples);
    const view = new DataView(bytes.buffer);
    const writeStr = (offset, str) => {
      for (let i = 0; i < str.length; i += 1) view.setUint8(offset + i, str.charCodeAt(i));
    };
    writeStr(0, "RIFF");
    view.setUint32(4, 36 + samples, true);
    writeStr(8, "WAVE");
    writeStr(12, "fmt ");
    view.setUint32(16, 16, true); // PCM header size
    view.setUint16(20, 1, true); // PCM format
    view.setUint16(22, 1, true); // mono
    view.setUint32(24, 8000, true); // sample rate
    view.setUint32(28, 8000, true); // byte rate
    view.setUint16(32, 1, true); // block align
    view.setUint16(34, 8, true); // bits per sample
    writeStr(36, "data");
    view.setUint32(40, samples, true);
    for (let i = 0; i < samples; i += 1) view.setUint8(44 + i, 128); // 128 = silence for unsigned 8-bit PCM
    return URL.createObjectURL(new Blob([bytes], { type: "audio/wav" }));
  } catch {
    return "";
  }
})();

export const useMunchySoundManager = (apiClient = null) => {
  const [muted, setMuted] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });
  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  const contextRef = useRef(null);
  const apiRef = useRef(apiClient);
  apiRef.current = apiClient;
  // One reusable <audio> element for every Munchy clip. Reusing a single element
  // (instead of `new Audio()` per line) lets us unlock it once inside a user
  // gesture so later clips play even under strict autoplay policies (iOS Safari).
  const audioRef = useRef(null);

  const ensureAudioEl = useCallback(() => {
    if (typeof window === "undefined" || !window.Audio) return null;
    if (!audioRef.current) {
      try {
        audioRef.current = new window.Audio();
        audioRef.current.preload = "auto";
      } catch {
        audioRef.current = null;
      }
    }
    return audioRef.current;
  }, []);

  const ensureAudio = useCallback(async () => {
    if (!contextRef.current) contextRef.current = createAudioContext();
    if (contextRef.current?.state === "suspended") {
      try {
        await contextRef.current.resume();
      } catch {
        // Resuming can reject if there was no gesture yet — safe to ignore.
      }
    }
    return contextRef.current;
  }, []);

  // Plays a short sequence of tones. `notes` is [{ freq, start, dur }].
  const playTones = useCallback(
    async (notes, type = "sine", peak = 0.06) => {
      if (mutedRef.current) return;
      const context = await ensureAudio();
      if (!context) return;
      const now = context.currentTime;
      notes.forEach(({ freq, start, dur }) => {
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        const startsAt = now + start;
        oscillator.type = type;
        oscillator.frequency.setValueAtTime(freq, startsAt);
        gain.gain.setValueAtTime(0.0001, startsAt);
        gain.gain.exponentialRampToValueAtTime(peak, startsAt + 0.015);
        gain.gain.exponentialRampToValueAtTime(0.0001, startsAt + dur);
        oscillator.connect(gain);
        gain.connect(context.destination);
        oscillator.start(startsAt);
        oscillator.stop(startsAt + dur + 0.02);
      });
    },
    [ensureAudio]
  );

  // A soft rising "boop" when a snack is lifted / lands on the tray.
  const playPop = useCallback(() => {
    playTones([{ freq: 520, start: 0, dur: 0.12 }, { freq: 720, start: 0.05, dur: 0.14 }], "sine", 0.05);
  }, [playTones]);

  // A round, wobbly "gulp" as Munchy swallows the correct snack, topped with a
  // bright rising "bloop" whose pitch climbs as the tummy fills (mirrors the
  // iOS GamePopAudio.pop(pitch:) success cue). `progress` is 0…1 through the meal.
  const playChomp = useCallback(
    (progress = 0.5) => {
      const clamped = Math.max(0, Math.min(1, progress));
      const top = 620 + clamped * 520;
      playTones(
        [
          { freq: 180, start: 0, dur: 0.12 },
          { freq: 130, start: 0.08, dur: 0.14 },
          { freq: 90, start: 0.16, dur: 0.16 },
          { freq: top, start: 0.05, dur: 0.16 },
        ],
        "sawtooth",
        0.07
      );
    },
    [playTones]
  );

  // A gentle, non-punishing low "thud" for a wrong feed (never a harsh buzz).
  const playWrong = useCallback(() => {
    playTones([{ freq: 200, start: 0, dur: 0.16 }, { freq: 150, start: 0.09, dur: 0.18 }], "triangle", 0.05);
  }, [playTones]);

  // A happy little arpeggio for meals complete / streak celebrations.
  const playCelebrate = useCallback(() => {
    playTones(
      [
        { freq: 523, start: 0, dur: 0.18 },
        { freq: 659, start: 0.12, dur: 0.18 },
        { freq: 784, start: 0.24, dur: 0.2 },
        { freq: 1047, start: 0.36, dur: 0.26 },
      ],
      "triangle",
      0.07
    );
  }, [playTones]);

  // The on-device understudy: instant, offline, and always available. Used on a
  // cold cache and whenever the real clip can't be fetched or played.
  const speakFallback = useCallback((text, pitch = FALLBACK_PITCH) => {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
    try {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = "en-US";
      utterance.rate = 0.95;
      utterance.pitch = pitch;
      utterance.volume = 1;
      window.speechSynthesis.speak(utterance);
    } catch {
      // Speech is a nice-to-have — the game plays fine without it.
    }
  }, []);

  // Fetch + cache the real Munchy clip for a line, returning the cached object
  // URL (or null if it can't be rendered). Concurrent callers for the same line
  // share one in-flight request, so warming a line and then awaiting it (e.g. the
  // intro) never kicks off a duplicate fetch. Best-effort: any failure just leaves
  // the synth understudy in charge for that line.
  const fetchClip = useCallback((text, voiceId = MUNCHY_VOICE_ID) => {
    const api = apiRef.current;
    if (!api?.quickTTS || !text) return Promise.resolve(null);
    const key = clipKey(voiceId, text);
    const cached = clipCache.get(key);
    if (cached) return Promise.resolve(cached);
    const pending = inFlight.get(key);
    if (pending) return pending;
    const request = (async () => {
      try {
        // quickTTS returns the raw base64 MP3 string (or null on any failure).
        const base64 = await api.quickTTS(text, voiceId);
        if (base64) {
          const url = base64ToBlobUrl(base64, "audio/mpeg");
          clipCache.set(key, url);
          return url;
        }
      } catch {
        // Endpoint down / offline — fall back to the synth voice.
      } finally {
        inFlight.delete(key);
      }
      return null;
    })();
    inFlight.set(key, request);
    return request;
  }, []);

  // Bumped every time playback is (re)started or stopped. An async speak that was
  // requested before a bump is stale — it must NOT grab the audio channel once it
  // finally loads, or a slow intro clip would blare over gameplay (or after the
  // child has already tapped past the greeting).
  const playEpochRef = useRef(0);

  const stopPlayback = useCallback(() => {
    playEpochRef.current += 1;
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      window.speechSynthesis.cancel();
    }
    if (audioRef.current) {
      try {
        audioRef.current.pause();
      } catch {
        // Pausing a not-yet-started element can throw — safe to ignore.
      }
    }
  }, []);

  // Unlock audio playback from within a user gesture (the "Let's feed Munchy!"
  // tap). Playing the silent clip on the shared element satisfies the browser's
  // autoplay policy, so the greeting clip that lands a beat later still plays in
  // Munchy's real voice instead of being blocked and falling back to the synth.
  const unlock = useCallback(() => {
    if (!SILENT_WAV_URL) return;
    const audio = ensureAudioEl();
    if (!audio) return;
    try {
      audio.src = SILENT_WAV_URL;
      const played = audio.play();
      if (played && typeof played.catch === "function") played.catch(() => {});
    } catch {
      // Best effort — if the unlock is refused the game still speaks (via synth).
    }
  }, [ensureAudioEl]);

  const playClip = useCallback(
    (url, fallbackText) => {
      stopPlayback();
      const audio = ensureAudioEl();
      if (!audio) {
        speakFallback(fallbackText);
        return;
      }
      try {
        audio.src = url;
        const played = audio.play();
        if (played && typeof played.catch === "function") {
          played.catch(() => speakFallback(fallbackText));
        }
      } catch {
        speakFallback(fallbackText);
      }
    },
    [stopPlayback, ensureAudioEl, speakFallback]
  );

  // Pre-generate clips for lines we know are coming (round prompts, item
  // labels, the intro / full lines) so they play in the real voice on first use.
  const warm = useCallback(
    (texts, voiceId = MUNCHY_VOICE_ID) => {
      if (mutedRef.current) return;
      (Array.isArray(texts) ? texts : [texts]).forEach((text) => {
        if (text) fetchClip(text, voiceId);
      });
    },
    [fetchClip]
  );

  // Munchy speaks — always in his own voice. Plays the cached real clip if we
  // have it; otherwise the synth understudy (same goofy Munchy pitch) speaks
  // instantly while the real clip downloads to take over next time. Warmed lines
  // (prompts, praise, food words) almost always hit the cache, so the child
  // hears the real Munchy voice, never the robot.
  const speak = useCallback(
    (text) => {
      if (mutedRef.current || !text) return;
      const key = clipKey(MUNCHY_VOICE_ID, text);
      const cached = clipCache.get(key);
      if (cached) {
        playClip(cached, text);
        return;
      }
      speakFallback(text, FALLBACK_PITCH);
      fetchClip(text, MUNCHY_VOICE_ID); // the real voice takes over next time
    },
    [playClip, speakFallback, fetchClip]
  );
  // Reads a food word aloud when a tray plate is tapped — same Munchy voice.
  const speakWord = useCallback((text) => speak(text), [speak]);

  // Like `speak`, but for set-piece lines (the intro greeting) where the cache is
  // guaranteed cold: it WAITS for the real Munchy clip to download and plays that,
  // instead of blurting the robotic synth understudy first. A brief load is fine
  // here, and it guarantees the child hears the same real Munchy voice at the
  // intro as during play. Only truly failed fetches fall back to the synth.
  const speakReal = useCallback(
    async (text) => {
      if (mutedRef.current || !text) return;
      const cached = clipCache.get(clipKey(MUNCHY_VOICE_ID, text));
      if (cached) {
        playClip(cached, text);
        return;
      }
      // Remember where playback stood before we wait on the network. If anything
      // else speaks or stops (e.g. the child taps past the intro into the meal)
      // while the clip downloads, this request is stale — drop it silently rather
      // than let a late greeting talk over whatever is playing now.
      const epoch = playEpochRef.current;
      const url = await fetchClip(text, MUNCHY_VOICE_ID);
      if (mutedRef.current || playEpochRef.current !== epoch) return;
      if (url) playClip(url, text);
      else speakFallback(text, FALLBACK_PITCH);
    },
    [playClip, fetchClip, speakFallback]
  );

  const stop = useCallback(() => {
    stopPlayback();
  }, [stopPlayback]);

  const toggleMute = useCallback(() => {
    setMuted((current) => {
      const next = !current;
      if (next) stopPlayback();
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        // The game still works when storage is unavailable.
      }
      return next;
    });
  }, [stopPlayback]);

  return useMemo(
    () => ({ muted, toggleMute, playPop, playChomp, playWrong, playCelebrate, speak, speakReal, speakWord, warm, unlock, stop }),
    [muted, toggleMute, playPop, playChomp, playWrong, playCelebrate, speak, speakReal, speakWord, warm, unlock, stop]
  );
};
